import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { counterpartyService } from '../services/counterpartyService';
import { contractService } from '../services/contractService';
import { Counterparty } from '../types/counterparty';
import { Currency } from '../types/invoice';
import { Contract, ContractStatus, CreateContractRequest } from '../types/contract';
import { useTranslation } from '../hooks/useTranslation';

interface ContractsTabProps {
  companyId: string;
}

const ContractsTab: React.FC<ContractsTabProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ContractStatus>('ALL');
  const [counterpartyFilter, setCounterpartyFilter] = useState<'ALL' | string>('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);

  const { data: counterparties = [] } = useQuery({
    queryKey: ['counterparties', companyId],
    queryFn: () => counterpartyService.getCompanyCounterparties(companyId)
  });

  const params = useMemo(() => {
    const p = new URLSearchParams({
      page: '0',
      size: '200'
    });
    if (searchTerm.trim()) {
      p.set('searchTerm', searchTerm.trim());
    }
    if (statusFilter !== 'ALL') {
      p.set('status', statusFilter);
    }
    if (counterpartyFilter !== 'ALL') {
      p.set('counterpartyId', counterpartyFilter);
    }
    return p;
  }, [searchTerm, statusFilter, counterpartyFilter]);

  React.useEffect(() => {
    const key = `preselect_contract_counterparty_${companyId}`;
    const preselectedCounterpartyId = sessionStorage.getItem(key);
    if (preselectedCounterpartyId) {
      setCounterpartyFilter(preselectedCounterpartyId);
      sessionStorage.removeItem(key);
    }
  }, [companyId]);

  const { data: paginated, isLoading } = useQuery({
    queryKey: ['contracts', companyId, params.toString()],
    queryFn: () => contractService.getPaginated(companyId, params)
  });

  const contracts: Contract[] = paginated?.content || [];

  const createMutation = useMutation({
    mutationFn: (payload: CreateContractRequest) => contractService.create(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', companyId] });
      setIsModalOpen(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateContractRequest }) =>
      contractService.update(companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', companyId] });
      setIsModalOpen(false);
      setEditingContract(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contractService.delete(companyId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contracts', companyId] })
  });

  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => contractService.uploadFile(companyId, id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contracts', companyId] })
  });

  const deleteFileMutation = useMutation({
    mutationFn: (id: string) => contractService.deleteFile(companyId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contracts', companyId] })
  });

  const handleDelete = (contract: Contract) => {
    const confirmed = window.confirm(
      t('companies.contracts.deleteConfirm') || `Удалить контракт "${contract.contractNumber}"?`
    );
    if (!confirmed) return;
    deleteMutation.mutate(contract.id);
  };

  const handleDownloadFile = async (contract: Contract) => {
    try {
      const blob = await contractService.downloadFile(companyId, contract.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', contract.originalFileName || `contract-${contract.contractNumber}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.alert(t('common.error') || 'Ошибка');
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
  };
  const getStatusLabel = (status: ContractStatus) =>
    t(`companies.contracts.statuses.${status}`) || status;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('companies.tabs.contracts') || 'Контракты'}
        </h3>
        <button
          onClick={() => {
            setEditingContract(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('companies.contracts.add') || 'Добавить контракт'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('common.search') || 'Поиск'}</p>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('companies.contracts.search') || 'Поиск по номеру, названию, контрагенту...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.status') || 'Статус'}</p>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'ALL' | ContractStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="ALL">{t('common.all') || 'Все'}</option>
              {Object.values(ContractStatus).map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.counterparty') || 'Контрагент'}</p>
            <select
              value={counterpartyFilter}
              onChange={(e) => setCounterpartyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="ALL">{t('companies.contracts.allCounterparties') || 'Все контрагенты'}</option>
              {counterparties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500 flex items-center">
            {(t('companies.contracts.total') || 'Всего') + ': ' + (paginated?.totalElements ?? 0)}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-gray-600">{t('common.loading') || 'Загрузка...'}</div>
        ) : contracts.length === 0 ? (
          <div className="p-6 text-gray-600">{t('companies.contracts.empty') || 'Контракты не найдены'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2">{t('companies.contracts.number') || 'Номер'}</th>
                  <th className="text-left px-3 py-2">{t('companies.contracts.title') || 'Название'}</th>
                  <th className="text-left px-3 py-2">{t('companies.contracts.counterparty') || 'Контрагент'}</th>
                  <th className="text-left px-3 py-2">{t('companies.contracts.period') || 'Период'}</th>
                  <th className="text-left px-3 py-2">{t('companies.contracts.status') || 'Статус'}</th>
                  <th className="text-left px-3 py-2">{t('companies.contracts.file') || 'Файл'}</th>
                  <th className="text-right px-3 py-2">{t('common.actions') || 'Действия'}</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{contract.contractNumber}</td>
                    <td className="px-3 py-2">{contract.title}</td>
                    <td className="px-3 py-2">{contract.counterpartyName}</td>
                    <td className="px-3 py-2">{formatDate(contract.startDate) + ' - ' + formatDate(contract.endDate)}</td>
                    <td className="px-3 py-2">{getStatusLabel(contract.status)}</td>
                    <td className="px-3 py-2">
                      {contract.originalFileName ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadFile(contract)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {contract.originalFileName}
                          </button>
                          <button
                            onClick={() => deleteFileMutation.mutate(contract.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            {t('common.delete') || 'Удалить'}
                          </button>
                        </div>
                      ) : (
                        <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
                          {t('companies.contracts.uploadFile') || 'Загрузить'}
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
                              if (!allowedTypes.includes(file.type)) {
                                window.alert(t('companies.contracts.fileTypeError') || 'Разрешены только PDF, PNG, JPG, JPEG');
                                return;
                              }
                              if (file.size > 10 * 1024 * 1024) {
                                window.alert(t('companies.contracts.fileSizeError') || 'Размер файла не должен превышать 10MB');
                                return;
                              }
                              uploadMutation.mutate({ id: contract.id, file });
                            }}
                          />
                        </label>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button
                        onClick={() => {
                          setEditingContract(contract);
                          setIsModalOpen(true);
                        }}
                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        {t('common.edit') || 'Редактировать'}
                      </button>
                      <button
                        onClick={() => handleDelete(contract)}
                        className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        {t('common.delete') || 'Удалить'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <ContractFormModal
          counterparties={counterparties}
          preselectedCounterpartyId={counterpartyFilter !== 'ALL' ? counterpartyFilter : undefined}
          initial={editingContract}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onClose={() => {
            setIsModalOpen(false);
            setEditingContract(null);
          }}
          onSubmit={(payload) => {
            if (editingContract) {
              updateMutation.mutate({ id: editingContract.id, payload });
              return;
            }
            createMutation.mutate(payload);
          }}
        />
      )}
    </div>
  );
};

interface ContractFormModalProps {
  counterparties: Counterparty[];
  preselectedCounterpartyId?: string;
  initial: Contract | null;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateContractRequest) => void;
}

const ContractFormModal: React.FC<ContractFormModalProps> = ({
  counterparties,
  preselectedCounterpartyId,
  initial,
  isLoading,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateContractRequest>({
    counterpartyId: initial?.counterpartyId || preselectedCounterpartyId || counterparties[0]?.id || '',
    contractNumber: initial?.contractNumber || '',
    title: initial?.title || '',
    startDate: initial?.startDate || '',
    endDate: initial?.endDate || '',
    status: initial?.status || ContractStatus.DRAFT,
    valueAmount: initial?.valueAmount,
    currency: initial?.currency || Currency.PLN,
    notes: initial?.notes || ''
  });

  React.useEffect(() => {
    if (!initial && preselectedCounterpartyId) {
      setForm((prev) => ({ ...prev, counterpartyId: preselectedCounterpartyId }));
    }
  }, [initial, preselectedCounterpartyId]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.counterpartyId || !form.contractNumber || !form.title) {
      window.alert(t('common.fillRequired') || 'Заполните обязательные поля');
      return;
    }
    onSubmit(form);
  };

  const getStatusLabel = (status: ContractStatus) =>
    t(`companies.contracts.statuses.${status}`) || status;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <form onSubmit={submit} className="p-5 space-y-4">
          <h4 className="text-lg font-semibold">
            {initial ? (t('companies.contracts.edit') || 'Редактировать контракт') : (t('companies.contracts.add') || 'Добавить контракт')}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.counterparty') || 'Контрагент'} *</p>
              <select
                value={form.counterpartyId}
                onChange={(e) => setForm({ ...form, counterpartyId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                {counterparties.length === 0 ? (
                  <option value="">{t('companies.contracts.noCounterparties') || 'Нет контрагентов'}</option>
                ) : (
                  counterparties.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.number') || 'Номер контракта'} *</p>
              <input
                value={form.contractNumber}
                onChange={(e) => setForm({ ...form, contractNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.title') || 'Название'} *</p>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.status') || 'Статус'}</p>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as ContractStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.values(ContractStatus).map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.startDate') || 'Дата начала'}</p>
              <input
                type="date"
                value={form.startDate || ''}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.endDate') || 'Дата окончания'}</p>
              <input
                type="date"
                value={form.endDate || ''}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.contracts.valueAmount') || 'Сумма контракта'}</p>
              <input
                type="number"
                step="0.01"
                value={form.valueAmount ?? ''}
                onChange={(e) => setForm({ ...form, valueAmount: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.currency') || 'Валюта'}</p>
              <select
                value={form.currency || Currency.PLN}
                onChange={(e) => setForm({ ...form, currency: e.target.value as Currency })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.values(Currency).map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <textarea
            value={form.notes || ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={t('companies.contracts.notes') || 'Заметки'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={3}
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              {t('common.cancel') || 'Отмена'}
            </button>
            <button
              type="submit"
              disabled={isLoading || counterparties.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? (t('common.saving') || 'Сохранение...') : (t('common.save') || 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractsTab;

